import {
  AddShoppingCartOutlined,
  CheckCircleOutlineOutlined,
  ExpandMoreOutlined,
  Inventory2Outlined,
  LocalShippingOutlined,
  PlayArrowOutlined,
  SearchOutlined,
  VisibilityOutlined
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DeliveryMission,
  EligibleDeliveryPreparation,
  OutboundRepackaging,
  completeDirectDeliveryMission,
  completeDeliveryMission,
  completeOutboundRepackaging,
  createDirectDeliveryMission,
  createOutboundRepackaging,
  createTrolleyDeliveryMission,
  loadAvailableDeliveryHandlingEquipment,
  loadDeliveryClients,
  loadDeliveryMissions,
  loadGeneratedDeliveryDocumentContent,
  loadGeneratedDeliveryDocuments,
  loadEligibleDeliveryPreparations,
  loadOutboundRepackagingOptions,
  loadOutboundRepackagings,
  loadProcessPoints,
  takeOutboundRepackaging,
  takeDeliveryMission
} from "./api";
import { ListRowActions } from "./ListRowActions";
import { PageHeader } from "./PageHeader";
import { MobileActionBar } from "./MobileActionBar";
import { MobileOperationalHeader } from "./MobileOperationalHeader";
import { ResponsiveStatusTabs } from "./ResponsiveStatusTabs";
import { SourceTrace } from "./SourceTrace";
import { ScanInput } from "./ScanInput";
import { operationalStatusColor, operationalStatusLabel } from "./operationalStatus";
import { setDiagnosticContext } from "./terminalDiagnostics";
import { formatMeasurement } from "./measurements";
import { useMeasurementSettings } from "./useMeasurementSettings";

type DeliveryTab = "consolidate" | "repackaging" | "missions" | "completed";
type CreationMode = "DIRECT" | "TROLLEY" | "REPACK";

function formatQuantity(value: number, locale: string) {
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 6
  }).format(value);
}

export function DeliveryPage({
  onBack, permissions, initialMissionId = null, initialOpenDetail = false
}: {
  onBack: () => void;
  permissions: string[];
  initialMissionId?: string | null;
  initialOpenDetail?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { configuration: measurements } = useMeasurementSettings();
  const locale = i18n.resolvedLanguage?.startsWith("en") ? "en-GB" : "fr-FR";
  const queryClient = useQueryClient();
  const theme = useTheme();
  const compactDelivery = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedPickingIds, setExpandedPickingIds] = useState<string[]>([]);
  const [tab, setTab] = useState<DeliveryTab>("consolidate");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [creationMode, setCreationMode] =
    useState<CreationMode>("TROLLEY");
  const [destinationId, setDestinationId] = useState("");
  const [handlingEquipmentId, setHandlingEquipmentId] = useState("");
  const [handlingEquipmentIdentifier, setHandlingEquipmentIdentifier] = useState("");
  const [priority, setPriority] = useState("100");
  const [repackingPointId, setRepackingPointId] = useState("");
  const [targetPackagingId, setTargetPackagingId] = useState("");
  const [repackAction, setRepackAction] =
    useState<OutboundRepackaging | null>(null);
  const [sourceHuScans, setSourceHuScans] = useState("");
  const [missionAction, setMissionAction] =
    useState<DeliveryMission | null>(null);
  const [claimMission, setClaimMission] =
    useState<DeliveryMission | null>(null);
  const initialMissionOpened = useRef(false);
  const [trolleyScan, setTrolleyScan] = useState("");
  const [destinationScan, setDestinationScan] = useState("");
  const [handlingUnitScans, setHandlingUnitScans] = useState("");
  const [search, setSearch] = useState("");
  const [expandedMissionIds, setExpandedMissionIds] = useState<string[]>([]);
  const [mobileSelection, setMobileSelection] = useState<{
    kind: "mission" | "repack";
    id: string;
  } | null>(null);

  const eligible = useQuery({
    queryKey: ["delivery-eligible-preparations"],
    queryFn: loadEligibleDeliveryPreparations
  });
  const missions = useQuery({
    queryKey: ["delivery-missions"], queryFn: loadDeliveryMissions
  });
  const clients = useQuery({
    queryKey: ["delivery-clients"], queryFn: loadDeliveryClients
  });
  const outboundRepackagings = useQuery({
    queryKey: ["delivery-outbound-repackagings"],
    queryFn: loadOutboundRepackagings
  });
  const selected = (eligible.data ?? []).filter(
    (item) => selectedIds.includes(item.taskId)
  );
  const deliveryGroups = useMemo(() => {
    const groups = new Map<string, EligibleDeliveryPreparation[]>();
    (eligible.data ?? []).forEach((item) => {
      const key = item.outboundOrderId ?? `PREPARATION:${item.orderId}`;
      groups.set(key, [...(groups.get(key) ?? []), item]);
    });
    return [...groups.entries()].map(([key, items]) => ({
      key,
      items,
      orderNumber: items[0].outboundOrderNumber ?? items[0].orderNumber,
      externalReference: items[0].outboundExternalReference,
      clientCode: items[0].clientCode,
      destinationId: items[0].deliveryDestinationId,
      destinationCode: items[0].deliveryDestinationCode,
      destinationName: items[0].deliveryDestinationName
    }));
  }, [eligible.data]);
  const normalizedSearch = search.trim().toLocaleLowerCase(locale);
  const filteredDeliveryGroups = useMemo(
    () => !normalizedSearch ? deliveryGroups : deliveryGroups.filter((group) =>
      [
        group.orderNumber,
        group.externalReference,
        group.clientCode,
        group.destinationCode,
        group.destinationName,
        ...group.items.flatMap((item) => [
          item.taskNumber,
          item.handlingUnitNumber,
          item.itemCode,
          item.itemName,
          item.warehouseCode,
          item.sourceProcessPointCode
        ])
      ].some((value) => (value ?? "").toLocaleLowerCase(locale)
        .includes(normalizedSearch))
    ),
    [deliveryGroups, normalizedSearch, locale]
  );
  const reference = selected[0];
  const processPoints = useQuery({
    queryKey: ["process-points", reference?.warehouseId],
    queryFn: () => loadProcessPoints(reference!.warehouseId),
    enabled: Boolean(reference?.warehouseId)
  });
  const packagingOptions = useQuery({
    queryKey: ["delivery-outbound-repackaging-options", reference?.taskId],
    queryFn: () => loadOutboundRepackagingOptions(reference!.taskId),
    enabled: Boolean(reference?.taskId)
  });
  const availableHandlingEquipment = useQuery({
    queryKey: ["delivery-available-handling-equipment", reference?.warehouseId],
    queryFn: () => loadAvailableDeliveryHandlingEquipment(reference!.warehouseId),
    enabled: Boolean(reference?.warehouseId && createOpen
      && creationMode === "TROLLEY")
  });
  const destinations = (clients.data ?? [])
    .filter((client) => !reference?.clientId
      || client.id === reference.clientId)
    .flatMap((client) => client.destinations)
    .filter((destination) =>
      destination.status === "ACTIVE"
    );
  const eligibleDestinations = creationMode === "TROLLEY"
    ? destinations.filter((destination) => destination.type === "POU")
    : destinations;
  const filteredMissions = useMemo(
    () => (missions.data ?? []).filter((mission) => {
      const statusMatches = tab === "missions"
        ? ["OPEN", "IN_PROGRESS", "EXCEPTION"].includes(mission.status)
        : ["COMPLETED", "CANCELLED"].includes(mission.status);
      if (!statusMatches || !normalizedSearch) return statusMatches;
      return [
        mission.missionNumber,
        mission.consolidationNumber,
        mission.trolleyNumber,
        mission.repackedHandlingUnitNumber,
        mission.clientCode,
        mission.warehouseCode,
        mission.destinationCode,
        mission.destinationName,
        mission.status,
        ...mission.lines.flatMap((line) => [
          line.preparationTaskNumber,
          line.handlingUnitNumber,
          line.itemCode,
          line.itemName
        ])
      ].some((value) => (value ?? "").toLocaleLowerCase(locale)
        .includes(normalizedSearch));
    }),
    [missions.data, normalizedSearch, tab, locale]
  );
  useEffect(() => {
    if (!initialMissionId || initialMissionOpened.current || !missions.data) return;
    const target = missions.data.find((mission) =>
      mission.id === initialMissionId
    );
    if (target) {
      initialMissionOpened.current = true;
      setTab(target.status === "COMPLETED" ? "completed" : "missions");
      if (initialOpenDetail) {
        if (target.status === "OPEN") setClaimMission(target);
        else setMissionAction(target);
      }
      window.setTimeout(() => document.getElementById(
        `delivery-${initialMissionId}`
      )?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
    }
  }, [initialMissionId, initialOpenDetail, missions.data]);

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["delivery-eligible-preparations"]
      }),
      queryClient.invalidateQueries({ queryKey: ["delivery-missions"] }),
      queryClient.invalidateQueries({
        queryKey: ["delivery-outbound-repackagings"]
      }),
      queryClient.invalidateQueries({ queryKey: ["stock-by-item"] })
    ]);
  };
  const create = useMutation<DeliveryMission | OutboundRepackaging, Error>({
    mutationFn: () => creationMode === "TROLLEY"
      ? createTrolleyDeliveryMission(
        selectedIds, destinationId, handlingEquipmentId || null,
        Number(priority), handlingEquipmentIdentifier.trim() || null
      )
      : creationMode === "REPACK"
        ? createOutboundRepackaging(
          selectedIds, destinationId, repackingPointId,
          targetPackagingId, Number(priority)
        )
      : createDirectDeliveryMission(
        selectedIds, destinationId, Number(priority)
      ),
    onSuccess: async () => {
      setSelectedIds([]);
      setDestinationId("");
      setHandlingEquipmentId("");
      setHandlingEquipmentIdentifier("");
      setPriority("100");
      setRepackingPointId("");
      setTargetPackagingId("");
      setCreateOpen(false);
      setTab(creationMode === "REPACK" ? "repackaging" : "missions");
      await refresh();
    }
  });
  const takeRepack = useMutation({
    mutationFn: (task: OutboundRepackaging) =>
      takeOutboundRepackaging(task.id, task.version),
    onSuccess: refresh
  });
  const completeRepack = useMutation({
    mutationFn: () => completeOutboundRepackaging(
      repackAction!.id, repackAction!.version,
      sourceHuScans.split(/[\n,;]+/)
        .map((value) => value.trim()).filter(Boolean)
    ),
    onSuccess: async () => {
      setRepackAction(null);
      setSourceHuScans("");
      setTab("missions");
      await refresh();
    }
  });
  const take = useMutation({
    mutationFn: (mission: DeliveryMission) =>
      takeDeliveryMission(mission.id, mission.version),
    onSuccess: refresh
  });
  const complete = useMutation({
    mutationFn: () => missionAction!.consolidationMode === "TROLLEY"
      ? completeDeliveryMission(
        missionAction!.id, trolleyScan, destinationScan,
        missionAction!.version
      )
      : completeDirectDeliveryMission(
        missionAction!.id,
        handlingUnitScans.split(/[\n,;]+/)
          .map((value) => value.trim()).filter(Boolean),
        missionAction!.destinationType === "POU" ? destinationScan : null,
        missionAction!.version
      ),
    onSuccess: async () => {
      setMissionAction(null);
      setTrolleyScan("");
      setHandlingUnitScans("");
      setDestinationScan("");
      await refresh();
    }
  });

  function compatible(item: EligibleDeliveryPreparation) {
    return !reference
      || (item.clientId === reference.clientId
        && item.warehouseId === reference.warehouseId
        && item.sourceProcessPointId === reference.sourceProcessPointId);
  }
  function toggle(item: EligibleDeliveryPreparation) {
    setSelectedIds((current) => current.includes(item.taskId)
      ? current.filter((id) => id !== item.taskId)
      : [...current, item.taskId]);
  }
  function toggleGroup(items: EligibleDeliveryPreparation[]) {
    const selectable = items.filter(compatible);
    const allSelected = selectable.every((item) =>
      selectedIds.includes(item.taskId)
    );
    setSelectedIds((current) => allSelected
      ? current.filter((id) => !selectable.some((item) => item.taskId === id))
      : [...new Set([...current, ...selectable.map((item) => item.taskId)])]
    );
  }
  function openCompletion(mission: DeliveryMission) {
    setMissionAction(mission);
    setTrolleyScan("");
    setHandlingUnitScans("");
    setDestinationScan("");
  }

  function openCreation(mode: CreationMode) {
    setCreationMode(mode);
    const suggestedDestinations = [...new Set(selected
      .map((item) => item.deliveryDestinationId)
      .filter((id): id is string => Boolean(id)))];
    setDestinationId(suggestedDestinations.length === 1
      ? suggestedDestinations[0] : "");
    setHandlingEquipmentId("");
    setRepackingPointId("");
    setTargetPackagingId("");
    setCreateOpen(true);
  }

  useEffect(() => setMobileSelection(null), [tab]);
  const selectedMobileMission = mobileSelection?.kind === "mission"
    ? filteredMissions.find((mission) => mission.id === mobileSelection.id) ?? null
    : null;
  const activeRepackagings = (outboundRepackagings.data ?? []).filter((task) =>
    ["WAITING_TRANSFER", "OPEN", "IN_PROGRESS"].includes(task.status));
  const selectedMobileRepack = mobileSelection?.kind === "repack"
    ? activeRepackagings.find((task) => task.id === mobileSelection.id) ?? null
    : null;
  const mobilePrimaryLabel = tab === "consolidate"
    ? t("delivery.actions.trolleyMobile")
    : selectedMobileMission?.status === "OPEN" ? t("delivery.actions.take")
      : selectedMobileMission?.status === "IN_PROGRESS" ? t("delivery.actions.confirmDrop")
        : selectedMobileMission ? t("delivery.actions.view")
          : selectedMobileRepack?.status === "OPEN" ? t("delivery.actions.take")
            : selectedMobileRepack?.status === "IN_PROGRESS" ? t("delivery.actions.finish")
              : t("delivery.actions.select");
  const mobilePrimaryAvailable = tab === "consolidate"
    ? permissions.includes("delivery:create") && selectedIds.length > 0
    : Boolean(selectedMobileMission || (selectedMobileRepack
      && selectedMobileRepack.status !== "WAITING_TRANSFER"));
  useEffect(() => {
    const mission = missionAction ?? claimMission ?? selectedMobileMission;
    const selectedPreparations = selected.slice(0, 10);
    setDiagnosticContext({
      screen: t("delivery.module"),
      action: missionAction ? t("delivery.diagnostics.confirm")
        : claimMission ? t("delivery.diagnostics.take")
          : createOpen ? t("delivery.diagnostics.create")
            : t("delivery.diagnostics.tab", { tab: t(`delivery.tabs.${tab === "repackaging" ? "repack" : tab}`) }),
      entities: [
        ...(mission ? [{ type: "MISSION" as const,
          identifier: mission.missionNumber, status: mission.status }] : []),
        ...(mission?.lines ?? []).slice(0, 10).map((line) => ({
          type: "HU" as const, identifier: line.handlingUnitNumber
        })),
        ...selectedPreparations.map((item) => ({ type: "HU" as const,
          identifier: item.handlingUnitNumber }))
      ]
    });
  }, [missionAction, claimMission, selectedMobileMission, selected,
    createOpen, tab, t]);
  const executeMobilePrimary = () => {
    if (tab === "consolidate") {
      openCreation("TROLLEY");
    } else if (selectedMobileMission?.status === "OPEN") {
      take.mutate(selectedMobileMission);
    } else if (selectedMobileMission?.status === "IN_PROGRESS") {
      openCompletion(selectedMobileMission);
    } else if (selectedMobileMission) {
      setExpandedMissionIds((current) => current.includes(selectedMobileMission.id)
        ? current : [...current, selectedMobileMission.id]);
    } else if (selectedMobileRepack?.status === "OPEN") {
      takeRepack.mutate(selectedMobileRepack);
    } else if (selectedMobileRepack?.status === "IN_PROGRESS") {
      setRepackAction(selectedMobileRepack);
      setSourceHuScans("");
    }
  };
  const statusTabs = [
    { value: "consolidate" as const, label: t("delivery.tabs.consolidate"),
      mobileLabel: t("delivery.tabsMobile.consolidate"), count: eligible.data?.length ?? 0 },
    { value: "missions" as const, label: t("delivery.tabs.missions"), mobileLabel: t("delivery.tabsMobile.missions"),
      count: (missions.data ?? []).filter((mission) =>
        ["OPEN", "IN_PROGRESS", "EXCEPTION"].includes(mission.status)).length },
    { value: "repackaging" as const, label: t("delivery.tabs.repack"),
      mobileLabel: t("delivery.tabsMobile.repack"), count: activeRepackagings.length },
    { value: "completed" as const, label: t("delivery.tabs.completed"), mobileLabel: t("delivery.tabsMobile.completed"),
      count: (missions.data ?? []).filter((mission) =>
        ["COMPLETED", "CANCELLED"].includes(mission.status)).length }
  ];

  return (
    <Stack spacing={1.5} sx={{
      height: { xs: "100%", sm: "auto", md: "100%" }, minHeight: 0,
      overflow: { xs: "hidden", sm: "visible", md: "hidden" }
    }}>
      <MobileOperationalHeader moduleLabel={t("delivery.module")}
        search={search} onSearchChange={setSearch}
        searchPlaceholder={t("delivery.searchPlaceholder")} onBack={onBack} />
      <Box sx={{ flexShrink: 0, display: { xs: "none", sm: "block" } }}>
      <PageHeader title={t("delivery.module")} compact onBack={onBack}
        actions={tab === "consolidate"
          && permissions.includes("delivery:create") ? (
            <Stack direction="row" spacing={1}>
              <Button variant="outlined"
                data-testid="delivery-create-direct"
                disabled={selectedIds.length === 0}
                onClick={() => openCreation("DIRECT")}>
                {t("delivery.actions.direct")}
              </Button>
              <Button variant="contained"
                startIcon={<AddShoppingCartOutlined />}
                data-testid="delivery-create-trolley"
                disabled={selectedIds.length === 0}
                onClick={() => openCreation("TROLLEY")}>
                {t("delivery.actions.trolley")}
              </Button>
              <Button variant="outlined" startIcon={<Inventory2Outlined />}
                data-testid="delivery-create-repack"
                disabled={selectedIds.length === 0}
                onClick={() => openCreation("REPACK")}>
                {t("delivery.actions.repack")}
              </Button>
            </Stack>
          ) : undefined} />
      </Box>
      <ResponsiveStatusTabs value={tab} items={statusTabs} onChange={setTab} />
      <Card variant="outlined" sx={{ display: { xs: "none", sm: "block" },
        flexShrink: 0 }}>
        <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
          <TextField fullWidth size="small" label={t("common.filters.search")}
            placeholder={t("delivery.searchWide")}
            value={search} onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: <SearchOutlined color="action" sx={{ mr: 1 }} />
            }} />
        </CardContent>
      </Card>
      <Box sx={{ minHeight: 0,
        flex: { xs: "1 1 0", sm: "none", md: 1 },
        overflowY: { xs: "auto", sm: "visible", md: "auto" },
        pr: .5, pb: { xs: 11, md: 0 } }}>
        <Stack spacing={1}>
          {(eligible.isError || missions.isError) && (
            <Alert severity="error">
              {t("delivery.errors.load")}
            </Alert>
          )}
          {tab === "consolidate" && !eligible.isLoading
            && eligible.data?.length === 0 && (
              <Alert severity="info">
                {t("delivery.empty.consolidation")}
              </Alert>
            )}
          {tab === "consolidate" && filteredDeliveryGroups.map((group) => {
            const selectable = group.items.filter(compatible);
            const selectedCount = selectable.filter((item) =>
              selectedIds.includes(item.taskId)
            ).length;
            return (
              <Card key={group.key} sx={{ overflow: "hidden" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "44px minmax(0, 1fr)", md: "auto minmax(0, 1fr) auto" },
                  gap: { xs: .5, md: 1.5 }, alignItems: "center", px: 1.25, py: 1, bgcolor: "action.hover" }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <LocalShippingOutlined color="secondary" sx={{ display: { xs: "none", md: "block" } }} />
                    <Checkbox
                      checked={selectable.length > 0
                        && selectedCount === selectable.length}
                      indeterminate={selectedCount > 0
                        && selectedCount < selectable.length}
                      disabled={selectable.length === 0}
                      inputProps={{ "aria-label": t("delivery.compact.selectGroup", { number: group.orderNumber }) }}
                      sx={{ minWidth: 44, minHeight: 44 }}
                      onChange={() => toggleGroup(group.items)}
                    />
                  </Stack>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography fontWeight={800} sx={{ overflowWrap: "anywhere", fontSize: { xs: ".875rem", md: "1rem" } }}>
                      {t("delivery.labels.outboundOrder", { number: group.orderNumber })}
                      {group.externalReference
                        ? ` · ${group.externalReference}` : ""}
                    </Typography>
                    <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={2} rowGap={.25}>
                      <Typography variant="body2">
                        <Box component="span" sx={{ typography: "caption", color: "text.secondary" }}>{t("delivery.compact.client")} : </Box>
                        {group.clientCode ?? t("delivery.labels.chooseClient")}
                      </Typography>
                      <Typography variant="body2">
                        <Box component="span" sx={{ typography: "caption", color: "text.secondary" }}>{t("delivery.labels.destination")} : </Box>
                        {group.destinationName ?? group.destinationCode ?? t("delivery.labels.chooseDestination")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("delivery.labels.huCount", { count: group.items.length })} · {t("delivery.labels.itemCount", {
                          count: new Set(group.items.map((item) => item.itemCode)).size
                        })}
                      </Typography>
                    </Stack>
                  </Box>
                  <Chip label={selectedCount === group.items.length
                    ? t("delivery.labels.orderSelected") : t("delivery.labels.toConsolidate")}
                    variant="outlined" color={selectedCount === group.items.length
                      ? "success" : "default"} size={compactDelivery ? "small" : "medium"}
                    sx={{ gridColumn: { xs: 2, md: "auto" }, justifySelf: "end" }} />
                </Box>
                {compactDelivery ? (
                  <Stack>
                    {group.items.map((item) => {
                      const expanded = expandedPickingIds.includes(item.taskId);
                      return (
                        <Box key={item.taskId} sx={{ p: 1, borderTop: 1, borderColor: "divider",
                          opacity: compatible(item) ? 1 : .5 }}>
                          <Box sx={{ display: "grid", gridTemplateColumns: "44px minmax(0, 1fr) auto", columnGap: .75, alignItems: "start" }}>
                            <Checkbox checked={selectedIds.includes(item.taskId)}
                              data-testid={`delivery-select-${item.taskId}`}
                              inputProps={{ "aria-label": t("delivery.compact.selectHu", { number: item.handlingUnitNumber }) }}
                              disabled={!compatible(item)} onChange={() => toggle(item)}
                              sx={{ minWidth: 44, minHeight: 44 }} />
                            <Box sx={{ minWidth: 0, pt: .5, overflowWrap: "anywhere" }}>
                              <Typography variant="body2" fontWeight={800}>{item.handlingUnitNumber}</Typography>
                              <Typography variant="body2" fontWeight={650}>{item.itemCode}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{item.itemName}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {t("delivery.compact.source")} : <Box component="span" sx={{ color: "text.primary", fontWeight: 650 }}>{item.sourceProcessPointCode}</Box>
                              </Typography>
                            </Box>
                            <Stack alignItems="flex-end" spacing={.25} sx={{ pt: .5 }}>
                              <Typography variant="body2" fontWeight={850} sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                                {formatQuantity(item.quantity, locale)} {item.uom}
                              </Typography>
                              <Chip size="small" label={t("delivery.labels.ready")} variant="outlined" color="success" />
                              <IconButton aria-expanded={expanded} sx={{ minWidth: 44, minHeight: 44 }}
                                aria-label={t(expanded ? "delivery.compact.hidePicking" : "delivery.compact.showPicking", { number: item.handlingUnitNumber })}
                                onClick={() => setExpandedPickingIds((current) => expanded
                                  ? current.filter((id) => id !== item.taskId) : [...current, item.taskId])}>
                                <ExpandMoreOutlined sx={{ transform: expanded ? "rotate(180deg)" : "none" }} />
                              </IconButton>
                            </Stack>
                          </Box>
                          {expanded && (
                            <Box sx={{ pl: "50px", pt: .5, overflowWrap: "anywhere" }}>
                              <Typography variant="caption" color="text.secondary">{t("delivery.compact.picking")} : </Typography>
                              <Typography component="span" variant="body2" fontWeight={650}>{item.taskNumber}</Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                ) : <TableContainer>
                  <Table size="small" aria-label={group.orderNumber} sx={{ minWidth: 720,
                    "& th": { typography: "caption", color: "text.secondary", py: .5 },
                    "& td": { py: .75 }, "& td, & th": { borderColor: "divider" } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" />
                        <TableCell>{t("delivery.compact.hu")}</TableCell>
                        <TableCell>{t("delivery.compact.item")}</TableCell>
                        <TableCell align="right">{t("delivery.compact.quantity")}</TableCell>
                        <TableCell>{t("delivery.compact.source")}</TableCell>
                        <TableCell />
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.items.map((item) => {
                        const expanded = expandedPickingIds.includes(item.taskId);
                        return (
                          <Fragment key={item.taskId}>
                            <TableRow sx={{ opacity: compatible(item) ? 1 : .5 }}>
                              <TableCell padding="checkbox">
                                <Checkbox checked={selectedIds.includes(item.taskId)}
                                  data-testid={`delivery-select-${item.taskId}`}
                                  inputProps={{ "aria-label": t("delivery.compact.selectHu", { number: item.handlingUnitNumber }) }}
                                  disabled={!compatible(item)} onChange={() => toggle(item)} />
                              </TableCell>
                              <TableCell sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>{item.handlingUnitNumber}</TableCell>
                              <TableCell sx={{ minWidth: 200 }}>
                                <Typography variant="body2" fontWeight={800}>{item.itemCode}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", maxWidth: 360 }}>
                                  {item.itemName}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                                {formatQuantity(item.quantity, locale)} {item.uom}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>{item.sourceProcessPointCode}</TableCell>
                              <TableCell align="right"><Chip size="small" label={t("delivery.labels.ready")} variant="outlined" color="success" /></TableCell>
                              <TableCell padding="checkbox">
                                <IconButton size="small" aria-expanded={expanded}
                                  aria-label={t(expanded ? "delivery.compact.hidePicking" : "delivery.compact.showPicking", { number: item.handlingUnitNumber })}
                                  onClick={() => setExpandedPickingIds((current) => expanded
                                    ? current.filter((id) => id !== item.taskId) : [...current, item.taskId])}>
                                  <ExpandMoreOutlined sx={{ transform: expanded ? "rotate(180deg)" : "none" }} />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                            {expanded && (
                              <TableRow>
                                <TableCell colSpan={7} sx={{ bgcolor: "action.hover" }}>
                                  <Typography variant="caption" color="text.secondary">{t("delivery.compact.picking")} : </Typography>
                                  <Typography component="span" variant="body2" fontWeight={650}>{item.taskNumber}</Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>}
              </Card>
            );
          })}
          {tab !== "consolidate" && filteredMissions.length === 0
            && tab !== "repackaging"
            && !missions.isLoading && (
              <Alert severity="info">{t("delivery.empty.missions")}</Alert>
            )}
          {["missions", "completed"].includes(tab)
            && filteredMissions.map((mission) => (
            <Card key={mission.id} id={`delivery-${mission.id}`} sx={{
              p: 1.5,
              cursor: { xs: "pointer", sm: "default" },
              outline: { xs: mobileSelection?.kind === "mission"
                && mobileSelection.id === mission.id
                ? "2px solid" : "none", sm: "none" },
              outlineColor: "primary.main",
              borderColor: mission.id === initialMissionId
                ? "primary.main" : undefined,
              borderWidth: mission.id === initialMissionId ? 2 : undefined
            }} onClick={(event) => {
              if ((event.target as HTMLElement).closest(
                "button,a,[role='button']")) return;
              setMobileSelection({ kind: "mission", id: mission.id });
            }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                alignItems={{ md: "center" }}>
                <LocalShippingOutlined color="secondary" />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography fontWeight={800}>
                    {mission.missionNumber} ·{" "}
                    {mission.consolidationMode === "TROLLEY"
                      ? mission.trolleyNumber
                      : mission.consolidationMode === "REPACK"
                        ? mission.repackedHandlingUnitNumber
                        : t("delivery.labels.originalHu")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {mission.lines.length} HU · {mission.clientCode}
                    {" · "}{mission.destinationCode} · {mission.destinationName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {mission.lines.map((line) =>
                      line.handlingUnitNumber).join(" · ")}
                  </Typography>
                  <SourceTrace sources={mission.lines.map(
                    (line) => line.outboundOrderNumber
                  )} step={t("delivery.labels.deliveryStep", { number: mission.missionNumber })}
                    client={tab === "completed" ? undefined : mission.clientCode}
                    destination={tab === "completed" ? undefined
                      : mission.destinationName ?? mission.destinationCode} />
                </Box>
                <ListRowActions actions={
                  permissions.includes("delivery:execute")
                    && mission.status === "OPEN" ? (
                      <Button variant="outlined" startIcon={<PlayArrowOutlined />}
                        sx={{ display: { xs: "none", sm: "inline-flex" } }}
                        data-testid={`delivery-take-${mission.id}`}
                        disabled={take.isPending}
                        onClick={() => take.mutate(mission)}>
                        {t("delivery.actions.take")}
                      </Button>
                    ) : permissions.includes("delivery:execute")
                      && mission.status === "IN_PROGRESS" ? (
                        <Button variant="contained"
                          sx={{ display: { xs: "none", sm: "inline-flex" } }}
                          data-testid={`delivery-complete-${mission.id}`}
                          startIcon={<CheckCircleOutlineOutlined />}
                          onClick={() => openCompletion(mission)}>
                          {t("delivery.actions.confirmDrop")}
                        </Button>
                      ) : undefined
                } status={<Chip label={operationalStatusLabel(mission.status)}
                  color={operationalStatusColor(mission.status)} />} />
                <IconButton className="mobile-card-expand"
                  aria-label={t("delivery.aria.details")}
                  onClick={() => setExpandedMissionIds((current) =>
                    current.includes(mission.id)
                      ? current.filter((id) => id !== mission.id)
                      : [...current, mission.id]
                  )}>
                  <ExpandMoreOutlined sx={{ transition: "transform .2s",
                    transform: expandedMissionIds.includes(mission.id)
                      ? "rotate(180deg)" : "rotate(0deg)" }} />
                </IconButton>
              </Stack>
              <Collapse in={expandedMissionIds.includes(mission.id)} unmountOnExit>
                <DeliveryMissionDetails mission={mission} />
              </Collapse>
            </Card>
          ))}
          {tab === "repackaging"
            && (outboundRepackagings.data ?? [])
              .filter((task) => [
                "WAITING_TRANSFER", "OPEN", "IN_PROGRESS"
              ].includes(task.status))
              .filter((task) => !normalizedSearch || [
                task.consolidationNumber,
                task.clientCode,
                task.destinationCode,
                task.repackingProcessPointCode,
                task.targetPackagingCode,
                task.outputHandlingUnitNumber,
                task.status,
                ...task.sourceHandlingUnitNumbers
              ].some((value) => (value ?? "").toLocaleLowerCase(locale)
                .includes(normalizedSearch)))
              .map((task) => (
                <Card key={task.id} sx={{ p: 1.5,
                  cursor: { xs: "pointer", sm: "default" },
                  outline: { xs: mobileSelection?.kind === "repack"
                    && mobileSelection.id === task.id
                    ? "2px solid" : "none", sm: "none" },
                  outlineColor: "primary.main" }}
                  onClick={(event) => {
                    if ((event.target as HTMLElement).closest(
                      "button,a,[role='button']")) return;
                    setMobileSelection({ kind: "repack", id: task.id });
                  }}>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                    alignItems={{ md: "center" }}>
                    <Inventory2Outlined color="secondary" />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography fontWeight={800}>
                        {task.consolidationNumber} · {task.targetPackagingCode}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {task.sourceHandlingUnitNumbers.join(" · ")}
                        {" → "}{task.repackingProcessPointCode}
                        {" · "}{task.clientCode} · {task.destinationCode}
                      </Typography>
                    </Box>
                    <ListRowActions actions={task.status === "OPEN" ? (
                      <Button variant="outlined"
                        sx={{ display: { xs: "none", sm: "inline-flex" } }}
                        data-testid={`delivery-repack-take-${task.id}`}
                        onClick={() => takeRepack.mutate(task)}>
                        {t("delivery.actions.take")}
                      </Button>
                    ) : task.status === "IN_PROGRESS" ? (
                      <Button variant="contained"
                        sx={{ display: { xs: "none", sm: "inline-flex" } }}
                        data-testid={`delivery-repack-complete-${task.id}`}
                        onClick={() => {
                          setRepackAction(task);
                          setSourceHuScans("");
                        }}>
                        {t("delivery.actions.finish")}
                      </Button>
                    ) : undefined} status={<Chip
                      label={task.status === "WAITING_TRANSFER"
                        ? t("delivery.labels.transferRequired")
                        : task.status === "OPEN" ? t("delivery.labels.open") : t("delivery.labels.inProgress")}
                      color={operationalStatusColor(task.status)} />} />
                  </Stack>
                </Card>
              ))}
        </Stack>
      </Box>

      <MobileActionBar
        secondaryIcon={tab === "consolidate" ? <LocalShippingOutlined /> : undefined}
        secondaryLabel={t("delivery.actions.direct")} secondaryDisplayLabel={t("delivery.actions.directShort")}
        onSecondary={tab === "consolidate" && permissions.includes("delivery:create")
          && selectedIds.length > 0 ? () => openCreation("DIRECT") : undefined}
        additionalIcon={tab === "consolidate" ? <Inventory2Outlined /> : undefined}
        additionalLabel={t("delivery.actions.repack")} additionalDisplayLabel={t("delivery.actions.repackShort")}
        onAdditional={tab === "consolidate" && permissions.includes("delivery:create")
          && selectedIds.length > 0 ? () => openCreation("REPACK") : undefined}
        primaryLabel={mobilePrimaryLabel}
        primaryDisabled={!mobilePrimaryAvailable}
        onPrimary={executeMobilePrimary} />

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)}
        fullWidth maxWidth="sm">
        <DialogTitle>
          {creationMode === "TROLLEY"
            ? t("delivery.dialogs.createTrolley")
            : creationMode === "REPACK"
              ? t("delivery.dialogs.createRepack")
            : t("delivery.dialogs.createDirect")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">
              {t(creationMode === "TROLLEY" ? "delivery.help.trolleySelection"
                : creationMode === "REPACK" ? "delivery.help.repackSelection"
                  : "delivery.help.directSelection", {
                count: selected.length, station: reference?.sourceProcessPointCode ?? ""
              })}
            </Alert>
            {creationMode === "TROLLEY" && (
              <>
                <ScanInput
                  options={(availableHandlingEquipment.data ?? []).map((item) => ({
                    value: item.identifier,
                    label: `${item.identifier} · ${item.typeName}`
                      + ` · ${t("delivery.labels.capacity", {
                        capacity: formatMeasurement(item.maxLoadKg, "MASS",
                          measurements, locale)
                      })}`
                  }))}
                  value={handlingEquipmentIdentifier}
                  onChange={(value) => {
                    setHandlingEquipmentIdentifier(value);
                    const selectedEquipment = (availableHandlingEquipment.data ?? [])
                      .find((item) => item.identifier.toUpperCase()
                        === value.trim().toUpperCase());
                    setHandlingEquipmentId(selectedEquipment?.id ?? "");
                  }}
                  testId="delivery-handling-equipment"
                  required label={t("delivery.labels.physicalTrolley")}
                  helperText={t("delivery.help.chooseTrolley")}
                  textFieldProps={{ sx: { "& .MuiInputBase-root": { minHeight: 56 } } }}
                />
                {availableHandlingEquipment.isSuccess
                  && availableHandlingEquipment.data.length === 0 && (
                  <Alert severity="warning">
                    {t("delivery.help.noTrolley")}
                  </Alert>
                )}
              </>
            )}
            <TextField select required
              data-testid="delivery-destination"
              label={creationMode === "TROLLEY"
                ? t("delivery.labels.destinationPou") : t("delivery.labels.destination")}
              size="small"
              value={destinationId}
              onChange={(event) => setDestinationId(event.target.value)}
              sx={{ "& .MuiInputBase-root": { minHeight: 56 } }}>
              {eligibleDestinations.map((destination) => (
                <MenuItem key={destination.id} value={destination.id}>
                  {destination.code} · {destination.displayName}
                  {" · "}{destination.type === "POU" ? t("delivery.labels.pou") : t("delivery.labels.postalAddress")}
                </MenuItem>
              ))}
            </TextField>
            {creationMode === "REPACK" && (
              <>
                <TextField select required label={t("delivery.labels.repackingStation")}
                  data-testid="delivery-repacking-point"
                  value={repackingPointId}
                  onChange={(event) => setRepackingPointId(event.target.value)}
                  sx={{ "& .MuiInputBase-root": { minHeight: 56 } }}>
                  {(processPoints.data ?? [])
                    .filter((point) => point.status === "ACTIVE"
                      && point.type === "REPACKING")
                    .map((point) => (
                      <MenuItem key={point.id} value={point.id}>
                        {point.code} · {point.displayName}
                      </MenuItem>
                    ))}
                </TextField>
                <TextField select required label={t("delivery.labels.targetPackaging")}
                  data-testid="delivery-target-packaging"
                  value={targetPackagingId}
                  onChange={(event) => setTargetPackagingId(event.target.value)}
                  sx={{ "& .MuiInputBase-root": { minHeight: 56 } }}>
                  {(packagingOptions.data ?? []).map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.code} · {option.displayName} · {option.uom}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            )}
            <TextField type="number" required label={t("delivery.labels.priority")} size="small"
              value={priority} onChange={(event) => setPriority(event.target.value)}
              sx={{ "& .MuiInputBase-root": { minHeight: 56 } }} />
            {create.isError && (
              <Alert severity="error">
                {create.error instanceof Error
                  ? create.error.message
                  : creationMode === "TROLLEY"
                    ? t("delivery.errors.reserveTrolley")
                    : creationMode === "REPACK"
                      ? t("delivery.errors.createRepack")
                      : t("delivery.errors.createDirect")}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setCreateOpen(false)}
            sx={{ width: 132, height: 48 }}>
            {t("common.actions.cancel")}
          </Button>
          <Button variant="contained" disabled={!destinationId
            || create.isPending || (creationMode === "REPACK"
              && (!repackingPointId || !targetPackagingId))
            || (creationMode === "TROLLEY"
              && !handlingEquipmentIdentifier.trim())}
            data-testid="delivery-create-confirm"
            sx={{ width: 132, height: 48 }}
            onClick={() => create.mutate()}>
            {t("delivery.actions.create")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(missionAction)}
        onClose={() => setMissionAction(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {missionAction?.destinationType === "POU"
            ? t("delivery.dialogs.confirmPou")
            : t("delivery.dialogs.confirmShipment")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {missionAction?.consolidationMode === "TROLLEY" ? (
              <>
                <Alert severity="info">
                  {t("delivery.help.scanTrolleyPou")}
                </Alert>
                <ScanInput autoFocus required label={t("delivery.scan.trolley")}
                  testId="delivery-trolley-scan"
                  placeholder={missionAction?.trolleyNumber ?? ""}
                  value={trolleyScan}
                  onChange={setTrolleyScan} />
              </>
            ) : (
              <>
                <Alert severity="info">
                  {missionAction?.consolidationMode === "REPACK"
                    ? t("delivery.help.scanNewHu")
                    : t("delivery.help.scanOriginalHu")}
                </Alert>
                <TextField autoFocus required multiline minRows={3}
                  data-testid="delivery-hu-scans"
                  label={t("delivery.labels.scannedHu", { count:
                    missionAction?.consolidationMode === "REPACK"
                      ? 1 : missionAction?.lines.length ?? 0 })}
                  placeholder={missionAction?.consolidationMode === "REPACK"
                    ? missionAction.repackedHandlingUnitNumber ?? ""
                    : missionAction?.lines
                      .map((line) => line.handlingUnitNumber).join("\n")}
                  value={handlingUnitScans}
                  onChange={(event) =>
                    setHandlingUnitScans(event.target.value)} />
              </>
            )}
            {missionAction?.destinationType === "POU" && (
              <ScanInput required label={t("delivery.scan.pou")}
                testId="delivery-destination-scan"
                placeholder={missionAction?.destinationScanCode ?? ""}
                value={destinationScan}
                onChange={setDestinationScan} />
            )}
            {complete.isError && (
              <Alert severity="error">
                {complete.error instanceof Error
                  ? complete.error.message
                  : t("delivery.errors.confirmation")}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMissionAction(null)}>{t("common.actions.close")}</Button>
          <Button variant="contained" color="success"
            data-testid="delivery-complete-confirm"
            disabled={complete.isPending
              || (missionAction?.consolidationMode === "TROLLEY"
                ? !trolleyScan.trim() || !destinationScan.trim()
                : !handlingUnitScans.trim()
                  || (missionAction?.destinationType === "POU"
                    && !destinationScan.trim()))}
            onClick={() => complete.mutate()}>
            {missionAction?.destinationType === "POU"
              ? t("delivery.actions.confirmDrop") : t("delivery.actions.confirmShipment")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(repackAction)}
        onClose={() => setRepackAction(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t("delivery.dialogs.finishRepack")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">
              {t("delivery.help.repackScan", { packaging: repackAction?.targetPackagingCode ?? "" })}
            </Alert>
            <TextField autoFocus required multiline minRows={4}
              data-testid="delivery-repack-source-scans"
              label={t("delivery.labels.sourceHu", { count: repackAction?.sourceHandlingUnitNumbers.length ?? 0 })}
              placeholder={repackAction?.sourceHandlingUnitNumbers.join("\n")}
              value={sourceHuScans}
              onChange={(event) => setSourceHuScans(event.target.value)} />
            {completeRepack.isError && (
              <Alert severity="error">
                {completeRepack.error instanceof Error
                  ? completeRepack.error.message
                  : t("delivery.errors.completeRepack")}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRepackAction(null)}>{t("common.actions.close")}</Button>
          <Button variant="contained" color="success"
            data-testid="delivery-repack-complete-confirm"
            disabled={!sourceHuScans.trim() || completeRepack.isPending}
            onClick={() => completeRepack.mutate()}>
            {t("delivery.actions.createHuMission")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(claimMission)}
        onClose={() => setClaimMission(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t("delivery.dialogs.takeMission")}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info">
            {claimMission?.consolidationMode === "TROLLEY"
              ? claimMission.trolleyNumber
              : claimMission?.consolidationMode === "REPACK"
                ? claimMission.repackedHandlingUnitNumber
                : t("delivery.labels.originalHu")}
            {" → "}{claimMission?.destinationCode}
            {" · "}{claimMission?.lines.length ?? 0} HU
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClaimMission(null)}>{t("common.actions.cancel")}</Button>
          <Button variant="contained" disabled={take.isPending}
            onClick={() => take.mutate(claimMission!, {
              onSuccess: () => setClaimMission(null)
            })}>
            {t("delivery.actions.confirmTake")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function DeliveryMissionDetails({ mission }: { mission: DeliveryMission }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage?.startsWith("en") ? "en-GB" : "fr-FR";
  const documents = useQuery({
    queryKey: ["generated-delivery-documents", mission.id],
    queryFn: () => loadGeneratedDeliveryDocuments(mission.id)
  });
  const [openError, setOpenError] = useState<string | null>(null);

  const openDocument = async (documentId: string) => {
    setOpenError(null);
    const preview = window.open("", "_blank");
    try {
      const blob = await loadGeneratedDeliveryDocumentContent(
        mission.id, documentId);
      const url = URL.createObjectURL(blob);
      if (preview) preview.location.href = url;
      else window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (cause) {
      preview?.close();
      setOpenError(cause instanceof Error
        ? cause.message : t("delivery.errors.documentOpen"));
    }
  };

  return (
    <Box sx={{ mt: 1.5, ml: { md: 7 }, p: 1.5,
      bgcolor: "action.hover", borderRadius: 1 }}>
      <Typography fontWeight={800} sx={{ mb: 1 }}>{t("delivery.labels.missionDetails")}</Typography>
      <Stack spacing={0.75}>
        {mission.lines.map((line) => (
          <Stack key={line.preparationTaskId}
            direction={{ xs: "column", md: "row" }} spacing={{ md: 1 }}>
            <Typography variant="body2" fontWeight={700} sx={{ minWidth: 155 }}>
              {line.handlingUnitNumber}
            </Typography>
            <Typography variant="body2">
              {line.itemCode} · {line.itemName} · {formatQuantity(line.quantity, locale)} {line.uom}
              {line.outboundOrderNumber ? ` · ${line.outboundOrderNumber}` : ""}
            </Typography>
          </Stack>
        ))}
      </Stack>
      <Typography fontWeight={800} sx={{ mt: 2, mb: 1 }}>
        {documents.data
          ? t("delivery.labels.documentsCount", { count: documents.data.length })
          : t("delivery.labels.documents")}
      </Typography>
      {documents.isLoading && <Typography variant="body2">{t("delivery.document.loading")}</Typography>}
      {documents.isError && <Alert severity="error">{t("delivery.errors.documents")}</Alert>}
      {openError && <Alert severity="error" sx={{ mb: 1 }}>{openError}</Alert>}
      {documents.data?.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t("delivery.empty.documents")}
        </Typography>
      )}
      <Stack spacing={1}>
        {documents.data?.map((document) => (
          <Stack key={document.id} direction={{ xs: "column", sm: "row" }}
            spacing={1} alignItems={{ sm: "center" }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body2" fontWeight={700}>
                {document.number} · {document.filename}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {document.templateNumber} v{document.templateVersion}
                {" · "}{new Date(document.generatedAt).toLocaleString(locale)}
              </Typography>
            </Box>
            <Button size="small" variant="outlined"
              startIcon={<VisibilityOutlined />}
              onClick={() => void openDocument(document.id)}>
              {t("delivery.actions.view")}
            </Button>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
